import * as React from 'react'
import { Button } from '~/client/components/ui/button'
import { Credenza, CredenzaContent, CredenzaHeader, CredenzaTitle, CredenzaBody, CredenzaFooter } from '~/client/components/ui/credenza'
import { BasilAddSolidIcon } from '~/client/components/icons'
import { useNavigate } from '@tanstack/react-router'

type SharedListOption = {
  id: string
  title: string
  spaceCount: number
  slug: string
}

type MySharedListsCredenzaProps = {
  open?: boolean
  onOpenChange?: (open: boolean) => void
  lists: SharedListOption[]
  isLoading?: boolean
  onCreateList?: () => void
}

export function MySharedListsCredenza({ open = false, onOpenChange, lists, isLoading, onCreateList }: MySharedListsCredenzaProps) {
  const navigate = useNavigate()

  return (
    <Credenza open={open} onOpenChange={onOpenChange}>
      <CredenzaContent className="sm:rounded-[8px] max-w-full md:max-w-[580px] max-h-[90dvh] min-h-0 flex flex-col">
        <CredenzaHeader>
          <CredenzaTitle className="text-[20px] font-semibold text-[#18003d]">Shared Lists</CredenzaTitle>
        </CredenzaHeader>
        <CredenzaBody className="p-[20px] flex-1 min-h-0 overflow-y-auto">
          <div className="rounded-[12px] border border-neutral-200 shadow-[0px_4px_4px_0px_rgba(0,0,0,0.04)] overflow-hidden bg-white max-h-[400px] overflow-y-auto">
            {isLoading ? (
              Array.from({ length: 4 }).map((_, idx) => (
                <React.Fragment key={`skeleton-${idx}`}>
                  <div className="px-4 py-[18px]">
                    <div className="h-4 w-3/4 bg-gray-200 rounded animate-pulse" />
                    <div className="mt-2 h-4 w-1/2 bg-gray-200 rounded animate-pulse" />
                  </div>
                  {idx < 3 ? <div className="h-px bg-[#e4e1e9]" /> : null}
                </React.Fragment>
              ))
            ) : lists.length > 0 ? (
              lists.map((list, idx) => (
                <React.Fragment key={list.id}>
                  <button
                    type="button"
                    onClick={() => {
                      navigate({ to: '/collection/$slug', params: { slug: list.slug } })
                      onOpenChange?.(false)
                    }}
                    className="w-full text-left px-4 py-[18px] flex items-center justify-between bg-white hover:bg-[rgba(138,61,255,0.06)]"
                  >
                    <div className="flex-1">
                      <div className="text-[14px] font-medium text-[#55534e] leading-[14px]">{list.title}</div>
                      <div className="mt-1 text-[14px] font-medium text-[#9b9b9a]">
                        {list.spaceCount} {list.spaceCount === 1 ? 'space' : 'spaces'}
                      </div>
                    </div>
                  </button>
                  {idx < lists.length - 1 ? <div className="h-px bg-[#e4e1e9]" /> : null}
                </React.Fragment>
              ))
            ) : (
              <div className="px-4 py-[18px] text-sm text-[#9b9b9a]">No shared lists yet</div>
            )}
          </div>
        </CredenzaBody>
        <CredenzaFooter className="p-[20px] pt-3">
          <Button
            variant="revel"
            className="w-full h-11 rounded-[12px]"
            onClick={() => {
              onOpenChange?.(false)
              onCreateList?.()
            }}
          >
            Create Shared List
            <BasilAddSolidIcon variant="white" className="size-6" />
          </Button>
        </CredenzaFooter>
      </CredenzaContent>
    </Credenza>
  )
}
